import moment from 'moment';
import type {
  TripsMonitoredUsageData,
  TripGroupRow,
  AlertTypeRow,
  AlertsByChannel,
  ChartData,
} from '../../../types/tripsMonitoredUsage';
import type { TripStatusApiUsageData, TripStatusQueryRow } from '../../../types/tripStatusApiUsage';
import {
  formatWeeklyLabelCompact,
  formatWeeklyLabelFull,
} from './formatWeeklyLabel';

// ─── Backend Response Types ──────────────────────────────────────────────────

/** Granularity as returned/expected by the upstream service (uppercase). */
type ApiGranularity = 'MONTHLY' | 'DAILY' | 'WEEKLY' | 'QUARTERLY';

/**
 * A single trend data point. Monthly buckets use `month` ("YYYY-MM"); daily,
 * weekly and quarterly buckets use `period` ("YYYY-MM-DD" or "YYYY-Qn").
 */
interface TrendPoint {
  month?: string;
  period?: string;
  count: number;
}

/** Per-period count used inside tripsByTripGroup / alertsByAlertType. */
interface PeriodCount {
  period: string;
  count: number;
}

interface TripGroupUsage {
  tripGroupName: string;
  groupReferenceNumber: string;
  periodCounts: PeriodCount[];
}

interface AlertTypeUsage {
  alertType: string;
  alertTypeDisplayName: string;
  periodCounts: PeriodCount[];
}


interface AlertChannelBreakdown {
  emailAlerts?: AlertTypeUsage[];
  apiAlerts?: AlertTypeUsage[];
  smsAlerts?: AlertTypeUsage[];
}


interface GranularityBucket {
  monthlyTrend?: TrendPoint[];
  dailyTrend?: TrendPoint[];
  weeklyTrend?: TrendPoint[];
  quarterlyTrend?: TrendPoint[];
  tripsByTripGroup: TripGroupUsage[];
  alertsByAlertType: AlertChannelBreakdown | [];
}

interface TripsAlertsUsageResponse {
  accountId: string;
  dateStart: string;
  dateEnd: string;
  granularity: ApiGranularity;
  totalTripsMonitored: number;
  totalAlertsSentApi: number;
  totalAlertsSentEmail: number;
  monthly: GranularityBucket | [];
  daily: GranularityBucket | [];
  weekly: GranularityBucket | [];
  quarterly: GranularityBucket | [];
}

interface TripStatusUsageResponse {
  accountId: string;
  dateStart: string;
  dateEnd: string;
  granularity: ApiGranularity;
  totalQueries: number;
  averageQueriesPerPeriod: number;
  maxQueries: number;
  maxQueriesPeriod: string | null;
  minQueries: number;
  minQueriesPeriod: string | null;
  monthlyTrend: TrendPoint[];
  dailyTrend: TrendPoint[];
  weeklyTrend: TrendPoint[];
  quarterlyTrend: TrendPoint[];
}

// ─── Granularity Lookups ─────────────────────────────────────────────────────

type BucketKey = 'monthly' | 'daily' | 'weekly' | 'quarterly';
type TrendKey = 'monthlyTrend' | 'dailyTrend' | 'weeklyTrend' | 'quarterlyTrend';

const BUCKET_KEY: Record<ApiGranularity, BucketKey> = {
  MONTHLY: 'monthly',
  DAILY: 'daily',
  WEEKLY: 'weekly',
  QUARTERLY: 'quarterly',
};

const TREND_KEY: Record<ApiGranularity, TrendKey> = {
  MONTHLY: 'monthlyTrend',
  DAILY: 'dailyTrend',
  WEEKLY: 'weeklyTrend',
  QUARTERLY: 'quarterlyTrend',
};

/** Normalize an arbitrary granularity value to a known ApiGranularity. */
function normalizeGranularity(granularity: string | undefined | null): ApiGranularity {
  const upper = (granularity || '').toUpperCase();
  if (upper === 'DAILY' || upper === 'WEEKLY' || upper === 'QUARTERLY') {
    return upper;
  }
  return 'MONTHLY';
}


function formatPeriodLabel(
  period: string,
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): string {
  if (!period) return '';
  switch (granularity) {
    case 'DAILY':
      return moment(period, 'YYYY-MM-DD').format('MMM D');
    case 'WEEKLY':
      return formatWeeklyLabelCompact(period, rangeStart, rangeEnd);
    case 'QUARTERLY': {
      const [year, quarterToken] = period.split('-');
      return quarterToken ? `${year} ${quarterToken}` : period;
    }
    case 'MONTHLY':
    default:
      return moment(period, 'YYYY-MM').format('MMM');
  }
}

/**
 * Full-length label shown on the chart x-axis (unlike `formatPeriodLabel`,
 * which produces the compact labels used in table columns):
 *  - daily: full date, e.g. "Aug 1, 2025"
 *  - weekly: full date range clamped to the selected report range,
 *    e.g. "Jul 1, 2026 - Jul 5, 2026" (see formatWeeklyLabel)
 *  - monthly: full month name, e.g. "August"
 *  - quarterly: quarter with year, e.g. "Q3 2025"
 */
function formatChartAxisLabel(
  period: string,
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): string {
  if (!period) return '';
  switch (granularity) {
    case 'DAILY':
      return moment(period, 'YYYY-MM-DD').format('MMM D, YYYY');
    case 'WEEKLY':
      return formatWeeklyLabelFull(period, rangeStart, rangeEnd);
    case 'QUARTERLY': {
      const [year, quarterToken] = period.split('-');
      return quarterToken ? `${quarterToken} ${year}` : period;
    }
    case 'MONTHLY':
    default:
      return moment(period, 'YYYY-MM').format('MMMM');
  }
}

/** Convert a period to a metric subtitle (e.g. "On Aug 15", "On Q2"). */
function formatPeriodSubtitle(
  period: string | null,
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): string {
  if (!period) return '';
  return `On ${formatPeriodLabel(period, granularity, rangeStart, rangeEnd)}`;
}

/** The raw period value of a trend point (monthly uses `month`, others `period`). */
function trendPeriod(point: TrendPoint): string {
  return point.period ?? point.month ?? '';
}

/** Pick the populated granularity bucket from the trips/alerts response. */
function selectBucket(
  response: TripsAlertsUsageResponse,
  granularity: ApiGranularity
): GranularityBucket {
  const bucket = response[BUCKET_KEY[granularity]];
  if (bucket && !Array.isArray(bucket)) {
    return bucket;
  }
  return { tripsByTripGroup: [], alertsByAlertType: [] };
}

/**
 * Merge alert rows from multiple channels into a single list, summing counts
 * per alert type per period. Used to build the "All Alerts" view.
 */
function aggregateChannelAlerts(channels: AlertTypeUsage[][]): AlertTypeUsage[] {
  const byType = new Map<string, AlertTypeUsage>();
  channels.forEach(list => {
    (list || []).forEach(alert => {
      const existing = byType.get(alert.alertType);
      if (!existing) {
        byType.set(alert.alertType, {
          alertType: alert.alertType,
          alertTypeDisplayName: alert.alertTypeDisplayName,
          periodCounts: (alert.periodCounts || []).map(pc => ({ ...pc })),
        });
        return;
      }
      (alert.periodCounts || []).forEach(pc => {
        const match = existing.periodCounts.find(e => e.period === pc.period);
        if (match) {
          match.count += pc.count;
        } else {
          existing.periodCounts.push({ ...pc });
        }
      });
    });
  });
  return Array.from(byType.values());
}


/**
 * Collect every distinct raw period across all channel alert lists, sorted
 * chronologically (raw "YYYY-MM-DD" / "YYYY-MM" / "YYYY-Qn" period strings
 * sort correctly as plain strings), then format each for display.
 */
function collectSortedPeriodLabels(
  channelLists: AlertTypeUsage[][],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): string[] {
  const periods = new Set<string>();
  channelLists.forEach(list => {
    (list || []).forEach(alert => {
      (alert.periodCounts || []).forEach(pc => periods.add(pc.period));
    });
  });
  return Array.from(periods)
    .sort()
    .map(period => formatPeriodLabel(period, granularity, rangeStart, rangeEnd));
}

function transformAlertsByChannel(
  alertsByAlertType: AlertChannelBreakdown | [],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): { channels: AlertsByChannel; periodColumns: string[] } {
  const channels: AlertChannelBreakdown = Array.isArray(alertsByAlertType)
    ? {}
    : alertsByAlertType || {};
  const email = channels.emailAlerts || [];
  const api = channels.apiAlerts || [];
  const sms = channels.smsAlerts || [];

  return {
    channels: {
      all: transformAlertTypesTable(aggregateChannelAlerts([email, api, sms]), granularity, rangeStart, rangeEnd),
      email: transformAlertTypesTable(email, granularity, rangeStart, rangeEnd),
      api: transformAlertTypesTable(api, granularity, rangeStart, rangeEnd),
      sms: transformAlertTypesTable(sms, granularity, rangeStart, rangeEnd),
    },
    periodColumns: collectSortedPeriodLabels([email, api, sms], granularity, rangeStart, rangeEnd),
  };
}

/**
 * Transform tripsByTripGroup to flat TripGroupRow[] for table display.
 * Each row has { tripGroup, [periodLabel]: count, ... }
 */
function transformTripGroupsTable(
  tripGroups: TripGroupUsage[],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): TripGroupRow[] {
  return tripGroups.map(group => {
    const row: TripGroupRow = { tripGroup: group.tripGroupName };
    (group.periodCounts || []).forEach(pc => {
      row[formatPeriodLabel(pc.period, granularity, rangeStart, rangeEnd)] = pc.count;
    });
    return row;
  });
}

/** Chronologically-sorted union of period labels across every trip group. */
function collectTripGroupPeriodLabels(
  tripGroups: TripGroupUsage[],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): string[] {
  const periods = new Set<string>();
  (tripGroups || []).forEach(group => {
    (group.periodCounts || []).forEach(pc => periods.add(pc.period));
  });
  return Array.from(periods)
    .sort()
    .map(period => formatPeriodLabel(period, granularity, rangeStart, rangeEnd));
}

/**
 * Transform alertsByAlertType to flat AlertTypeRow[] for table display.
 * Uses alertTypeDisplayName for the alertType field.
 */
function transformAlertTypesTable(
  alertTypes: AlertTypeUsage[],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): AlertTypeRow[] {
  return alertTypes.map(alert => {
    const row: AlertTypeRow = { alertType: alert.alertTypeDisplayName };
    (alert.periodCounts || []).forEach(pc => {
      row[formatPeriodLabel(pc.period, granularity, rangeStart, rangeEnd)] = pc.count;
    });
    return row;
  });
}

/**
 * Transform a trend array to ChartData format for the bar chart component.
 */
function transformChartData(
  trend: TrendPoint[],
  seriesName: string,
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): ChartData {
  return {
    categories: trend.map(point => formatChartAxisLabel(trendPeriod(point), granularity, rangeStart, rangeEnd)),
    periods: trend.map(point => trendPeriod(point)),
    rangeStart,
    rangeEnd,
    series: [
      {
        name: seriesName,
        data: trend.map(point => point.count),
      },
    ],
  };
}

/**
 * Transform a trend array to TripStatusQueryRow[] for table display.
 */
function transformQueriesTable(
  trend: TrendPoint[],
  granularity: ApiGranularity,
  rangeStart?: string,
  rangeEnd?: string
): TripStatusQueryRow[] {
  return trend.map(point => ({
    month: formatPeriodLabel(trendPeriod(point), granularity, rangeStart, rangeEnd),
    queries: point.count,
  }));
}

// ─── Main Transformation Functions ───────────────────────────────────────────

/**
 * Transform backend tripsAlertsUsageDashboard response to frontend
 * TripsMonitoredUsageData type. The response nests the period breakdown under a
 * granularity bucket (monthly/daily/weekly/quarterly); only the bucket matching
 * `response.granularity` is populated.
 */
export function transformTripsAlertsResponse(response: TripsAlertsUsageResponse): TripsMonitoredUsageData {
  const granularity = normalizeGranularity(response.granularity);
  const rangeStart = response.dateStart;
  const rangeEnd = response.dateEnd;
  const bucket = selectBucket(response, granularity);
  const trend = (bucket[TREND_KEY[granularity]] || []) as TrendPoint[];
  const { channels: alertsByChannel, periodColumns: alertPeriodColumns } = transformAlertsByChannel(
    bucket.alertsByAlertType || [],
    granularity,
    rangeStart,
    rangeEnd
  );

  return {
    metrics: {
      totalTripsMonitored: response.totalTripsMonitored,
      totalAlertsSent: response.totalAlertsSentApi,
      totalEmailsSent: response.totalAlertsSentEmail,
    },
    chartData: transformChartData(trend, 'Trips Monitored', granularity, rangeStart, rangeEnd),
    tripGroupsTable: transformTripGroupsTable(bucket.tripsByTripGroup || [], granularity, rangeStart, rangeEnd),
    tripGroupPeriodColumns: collectTripGroupPeriodLabels(
      bucket.tripsByTripGroup || [],
      granularity,
      rangeStart,
      rangeEnd
    ),
    alertsByChannel,
    alertPeriodColumns,
  };
}

/**
 * Transform backend tripStatusUsageDashboard response to frontend
 * TripStatusApiUsageData type. Trends are top-level and named after the
 * granularity (monthlyTrend/dailyTrend/weeklyTrend/quarterlyTrend).
 */
export function transformTripStatusResponse(response: TripStatusUsageResponse): TripStatusApiUsageData {
  const granularity = normalizeGranularity(response.granularity);
  const rangeStart = response.dateStart;
  const rangeEnd = response.dateEnd;
  const trend = (response[TREND_KEY[granularity]] || []) as TrendPoint[];

  return {
    metrics: {
      totalQueries: response.totalQueries,
      averageQueries: response.averageQueriesPerPeriod,
      maxQueries: response.maxQueries,
      maxQueriesDate: formatPeriodSubtitle(response.maxQueriesPeriod, granularity, rangeStart, rangeEnd),
      minQueries: response.minQueries,
      minQueriesDate: formatPeriodSubtitle(response.minQueriesPeriod, granularity, rangeStart, rangeEnd),
    },
    chartData: transformChartData(trend, 'Queries', granularity, rangeStart, rangeEnd),
    queriesTable: transformQueriesTable(trend, granularity, rangeStart, rangeEnd),
  };
}

// ─── Export Backend Response Types (for service layer) ──────────────────────

export type { TripsAlertsUsageResponse, TripStatusUsageResponse, ApiGranularity };
